<template>
  <div class="centered-container">
    <ShareLink v-if="instanceId" :instanceId="instanceId" />
    <h2 class="promo-heading">
      Rate each item with stars or veto items you don't want.
    </h2>

    <h2 class="sub-heading">{{ instanceData.description }}</h2>

    <p class="rating-instruction">Click stars to rate (1-5). Zero stars means you haven't rated this item yet.</p>

    <!-- Things Rating Box -->
    <div class="content-box">
      <div class="box-heading">
        <h3>Rate Things</h3>
        <span v-if="maxVetos > 0" class="veto-limit">Vetoes remaining: {{ vetosRemaining }}/{{ maxVetos }}</span>
      </div>
      <div class="box-content">
        <ul>
          <li v-for="thing in instanceData.things" :key="thing.id">
            <div class="list-item">
              <span>{{ thing.thing }}</span>
              <span class="actions">
                <!-- Star Rating -->
                <div class="star-rating">
                  <button
                    v-for="star in 5"
                    :key="star"
                    @click="setRating(thing.id, star)"
                    :disabled="movedOn || isVetoed(thing.id)"
                    class="star-button"
                    :class="{ active: getRating(thing.id) >= star, disabled: isVetoed(thing.id) }"
                  >
                    <StarIcon :size="28" :fill="getRating(thing.id) >= star ? '#ffc107' : '#ddd'" />
                  </button>
                </div>
                <!-- Veto Button -->
                <button
                  @click="toggleVeto(thing.id)"
                  :disabled="movedOn || (!isVetoed(thing.id) && vetosRemaining <= 0)"
                  class="veto-button"
                  :class="{ active: isVetoed(thing.id), disabled: !isVetoed(thing.id) && vetosRemaining <= 0 }"
                >
                  {{ isVetoed(thing.id) ? 'Vetoed' : 'Veto' }}
                </button>
              </span>
            </div>
          </li>
        </ul>
      </div>
    </div>

    <button @click="finishRank" class="finish-button" :disabled="(!canFinish && !movedOn) || isSaving">
      {{ isSaving ? 'Saving...' : (movedOn ? 'Edit ratings' : 'Done rating things') }}
    </button>
    <p v-if="!canFinish && !movedOn && finishDisabledReason" class="disabled-reason">{{ finishDisabledReason }}</p>

    <ParticipantList :instanceData="instanceData" />
  </div>
</template>

<script>
import ParticipantList from "@/components/ParticipantList.vue";
import ShareLink from "@/components/ShareLink.vue";
import StarIcon from "@/components/StarIcon.vue";

export default {
  components: {
    ParticipantList,
    ShareLink,
    StarIcon
  },
  props: {
    instanceData: {
      type: Object,
      default: null
    },
    name: {
      type: Object,
      default: null
    },
    instanceId: {
      type: String,
      default: null
    },
    addRanking: {
      type: Function,
      default: null
    },
    updateState: {
      type: Function,
      default: null
    },
    updateStateWithRankings: {
      type: Function,
      default: null
    },
  },
  data() {
    return {
      ratings: {}, // { thingId: stars (1-5) }
      vetos: [], // [thingId]
      maxVetos: 0,
      movedOn: false,
      isSaving: false
    };
  },
  mounted() {
    this.updateRatings(this.instanceData);
  },
  watch: {
    'instanceData': {
      handler(newInstanceData) {
        // Update all ratings data (this will update this.movedOn)
        this.updateRatings(newInstanceData);
      },
      deep: true,
    },
    'movedOn': {
      handler(newMovedOn, oldMovedOn) {
        // When movedOn changes AND we're saving, reset isSaving (server response received)
        // This handles both: adding to moveOn (false -> true) and removing from moveOn (true -> false)
        if (oldMovedOn !== undefined && oldMovedOn !== newMovedOn && this.isSaving) {
          this.isSaving = false;
        }
      },
    },
  },
  computed: {
    vetosRemaining() {
      return this.maxVetos - this.vetos.length;
    },
    canFinish() {
      // Button should be enabled if:
      // 1. Vetos are within limit
      // 2. User hasn't moved on yet (from ranking)
      // 3. State is 'rank' (all connected participants have finished adding things)
      if (this.vetos.length > this.maxVetos || this.movedOn) {
        return false;
      }
      // Can only finish ranking when state is 'rank' (all connected participants done adding things)
      return this.instanceData && this.instanceData.state && this.instanceData.state.state === 'rank';
    },
    finishDisabledReason() {
      if (!this.instanceData || !this.instanceData.state) {
        return null;
      }
      if (this.instanceData.state.state !== 'rank') {
        return 'Waiting for all participants to finish adding things...';
      }
      if (this.vetos.length > this.maxVetos) {
        return `You can only veto up to ${this.maxVetos} things (currently ${this.vetos.length})`;
      }
      if (this.movedOn) {
        return 'You have already finished ranking';
      }
      return null;
    },
  },
  methods: {
    updateRatings(instanceData) {
      if (!instanceData || !this.name) {
        return;
      }

      // Load existing ratings and vetos if they exist
      if (instanceData.rankings && instanceData.rankings[this.name.id]) {
        const userRanking = instanceData.rankings[this.name.id];
        this.ratings = userRanking.ratings ? { ...userRanking.ratings } : {};
        this.vetos = userRanking.vetos ? [...userRanking.vetos] : [];
      } else {
        // Initialize empty
        this.ratings = {};
        this.vetos = [];
      }

      // Calculate max vetos: floor((things.length - 1) / names.length)
      // Include all participants (connected and disconnected) in the calculation
      const totalParticipants = instanceData.names ? instanceData.names.length : 1;
      this.maxVetos = Math.floor((instanceData.things.length - 1) / totalParticipants);

      // movedOn should only be true if state is 'rank' AND user is in moveOn
      // If state is still 'things', user can still interact even if they've moved on from adding things
      const isInMoveOn = instanceData.state && instanceData.state.moveOn ? instanceData.state.moveOn.includes(this.name.id) : false;
      const isRankState = instanceData.state && instanceData.state.state === 'rank';
      this.movedOn = isRankState && isInMoveOn;
    },
    getRating(thingId) {
      return this.ratings[thingId] || 0;
    },
    isVetoed(thingId) {
      return this.vetos.includes(thingId);
    },
    setRating(thingId, stars) {
      if (this.movedOn || this.isVetoed(thingId)) {
        return;
      }
      // Optimistic update: update local state immediately
      if (this.ratings[thingId] === stars) {
        this.ratings[thingId] = 0;
      } else {
        this.ratings[thingId] = stars;
      }
      // Remove from vetos if it was vetoed
      if (this.isVetoed(thingId)) {
        this.vetos = this.vetos.filter(id => id !== thingId);
      }
      // Update instanceData.rankings optimistically
      // eslint-disable-next-line vue/no-mutating-props
      if (!this.instanceData.rankings) {
        // eslint-disable-next-line vue/no-mutating-props
        this.instanceData.rankings = {};
      }
      // eslint-disable-next-line vue/no-mutating-props
      if (!this.instanceData.rankings[this.name.id]) {
        // eslint-disable-next-line vue/no-mutating-props
        this.instanceData.rankings[this.name.id] = { ratings: {}, vetos: [] };
      }
      // eslint-disable-next-line vue/no-mutating-props
      this.instanceData.rankings[this.name.id].ratings = { ...this.ratings };
      // eslint-disable-next-line vue/no-mutating-props
      this.instanceData.rankings[this.name.id].vetos = [...this.vetos];
      
      // Save to server
      this.saveRanking();
    },
    toggleVeto(thingId) {
      if (this.movedOn) {
        return;
      }
      
      // Optimistic update: update local state immediately
      if (this.isVetoed(thingId)) {
        // Remove veto
        this.vetos = this.vetos.filter(id => id !== thingId);
        // Clear rating if it was set
        if (this.ratings[thingId]) {
          this.ratings[thingId] = 0;
        }
      } else {
        // Add veto (if we have remaining vetos)
        if (this.vetosRemaining > 0) {
          this.vetos.push(thingId);
          // Clear rating if it was set
          if (this.ratings[thingId]) {
            this.ratings[thingId] = 0;
          }
        }
      }
      
      // Update instanceData.rankings optimistically
      // eslint-disable-next-line vue/no-mutating-props
      if (!this.instanceData.rankings) {
        // eslint-disable-next-line vue/no-mutating-props
        this.instanceData.rankings = {};
      }
      // eslint-disable-next-line vue/no-mutating-props
      if (!this.instanceData.rankings[this.name.id]) {
        // eslint-disable-next-line vue/no-mutating-props
        this.instanceData.rankings[this.name.id] = { ratings: {}, vetos: [] };
      }
      // eslint-disable-next-line vue/no-mutating-props
      this.instanceData.rankings[this.name.id].ratings = { ...this.ratings };
      // eslint-disable-next-line vue/no-mutating-props
      this.instanceData.rankings[this.name.id].vetos = [...this.vetos];
      
      // Save to server
      this.saveRanking();
    },
    saveRanking() {
      // Send rating update to server
      if (this.addRanking) {
        this.addRanking(this.ratings, this.vetos);
      }
    },
    async finishRank() {
      // Prevent multiple clicks
      if (this.isSaving) {
        return;
      }

      this.isSaving = true;
      try {
        if (this.movedOn) {
          // Re-enable rating by removing user from moveOn
          // Send ratings with updateState for atomic operation (prevents race conditions)
          if (this.updateStateWithRankings) {
            await this.updateStateWithRankings(this.ratings, this.vetos); // This will remove them from moveOn
          } else {
            // Fallback to separate calls if method not available
            await this.addRanking(this.ratings, this.vetos);
            await this.updateState();
          }
        } else if (this.canFinish) {
          // Save final ratings/vetos and mark as done
          // Send ratings with updateState for atomic operation (prevents race conditions)
          if (this.updateStateWithRankings) {
            await this.updateStateWithRankings(this.ratings, this.vetos); // This will add them to moveOn
          } else {
            // Fallback to separate calls if method not available
            await this.addRanking(this.ratings, this.vetos);
            await this.updateState();
          }
        }
      } catch (error) {
        // If there's an error, reset isSaving
        this.isSaving = false;
        console.error('Error finishing rank:', error);
      }
      // isSaving will be reset when moveOn state changes (server response via WebSocket)
    }
  },
};
</script>

<style scoped>
ul {
  list-style-type: none;
  padding: 0;
  margin-top: 10px;
}

.list-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #ddd;
  padding: 10px 0;
}

li:last-child .list-item {
  border-bottom: none;
}

.actions {
  display: flex;
  align-items: center;
  gap: 15px;
}

.star-rating {
  display: flex;
  gap: 5px;
}

.star-button {
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  line-height: 1;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: opacity 0.2s;
}

.star-button .star-svg {
  width: 28px;
  height: 28px;
}

.star-button .star-svg polygon {
  fill: #ddd;
  transition: fill 0.2s;
}

.star-button:hover:not(:disabled) .star-svg polygon {
  fill: #ffc107;
}

.star-button.active .star-svg polygon {
  fill: #ffc107;
}

.star-button.disabled .star-svg polygon {
  fill: #ccc;
  cursor: not-allowed;
}

.star-button:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

.veto-button {
  padding: 8px 16px;
  border: 2px solid #e74c3c;
  border-radius: 4px;
  background-color: white;
  color: #e74c3c;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  font-size: 14px;
  min-width: 80px;
  text-align: center;
}

.veto-button:hover:not(:disabled):not(.active) {
  background-color: #fdf2f2;
}

.veto-button.active {
  background-color: #e74c3c;
  color: white;
}

.veto-button.disabled {
  border-color: #ccc;
  color: #ccc;
  cursor: not-allowed;
}

.veto-button:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

.box-heading {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.veto-limit {
  font-size: 14px;
  color: white;
  font-weight: normal;
}

.disabled-reason {
  margin-top: 10px;
  color: #666;
  font-size: 14px;
  font-style: italic;
  text-align: center;
}

.rating-instruction {
  margin: 15px 0;
  padding: 10px;
  background-color: #f8f9fa;
  border-left: 4px solid #3498db;
  color: #495057;
  font-size: 14px;
  border-radius: 4px;
}
</style>
